import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListPhoneApprovalsQueryKey,
  useListPhoneApprovals,
  useApprovePhoneNumber,
  useRejectPhoneNumber,
  getListApprovedNumbersQueryKey,
  useListApprovedNumbers,
  useDeleteGuardian,
  useUpdateGuardian,
  useLinkGuardianStudent,
  useUnlinkGuardianStudent,
  getListStudentsQueryKey,
  useListStudents,
  type PhoneApprovalDetail,
  type ApprovedNumber
} from '@cdf/api-client-react';
import { CheckIcon, PencilIcon, PlusIcon, TrashIcon, UserPlusIcon, XIcon } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PhoneIcon } from '@/lib/navigation';

const PREFERENCE_LABELS: Record<string, string> = {
  by_self: 'Sign out by self',
  wait_for_parent: 'Wait for parent',
  back_to_school: 'Back to school',
  with_person: 'Leave with named person',
  with_teacher: 'Leave with teacher'
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function StudentMultiSelect({
  students,
  selected,
  onToggle
}: {
  students: { id: string; firstName: string; lastInitial: string; group: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  if (students.length === 0) {
    return <p className="text-sm text-muted-foreground">No students available.</p>;
  }
  return (
    <div className="max-h-52 space-y-1 overflow-y-auto rounded-md border p-2">
      {students.map((s) => {
        const isSelected = selected.has(s.id);
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onToggle(s.id)}
            className={`flex w-full items-center gap-2 rounded-md border p-2 text-left text-sm transition-colors ${
              isSelected ? 'border-foreground bg-muted' : 'border-transparent hover:bg-muted/50'
            }`}
          >
            <span className="font-medium">
              {s.firstName} {s.lastInitial}.
            </span>
            <span className="text-muted-foreground">{s.group}</span>
            {isSelected && <CheckIcon className="ml-auto size-4" />}
          </button>
        );
      })}
    </div>
  );
}

function PendingTab({ onSwitchToApproved }: { onSwitchToApproved: () => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useListPhoneApprovals({ status: 'pending' });
  const { data: studentsData } = useListStudents();
  const { data: approvedData } = useListApprovedNumbers();

  const [approveTarget, setApproveTarget] = React.useState<PhoneApprovalDetail | null>(null);
  const [rejectTarget, setRejectTarget] = React.useState<PhoneApprovalDetail | null>(null);

  const approveMutation = useApprovePhoneNumber({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListPhoneApprovalsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListApprovedNumbersQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
        setApproveTarget(null);
        onSwitchToApproved();
      }
    }
  });
  const rejectMutation = useRejectPhoneNumber({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListPhoneApprovalsQueryKey() });
        setRejectTarget(null);
      }
    }
  });

  const requests = data?.requests ?? [];

  return (
    <div className="space-y-4">
      {isLoading && <p className="text-sm text-muted-foreground">Loading pending requests…</p>}
      {!isLoading && requests.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-sm text-muted-foreground">No pending approval requests.</p>
        </Card>
      )}
      {requests.map((req) => (
        <Card key={req.id} className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-semibold">{req.phoneNumber}</span>
            {req.latestParsedPreference && (
              <Badge variant="outline">{PREFERENCE_LABELS[req.latestParsedPreference] ?? req.latestParsedPreference}</Badge>
            )}
            <span className="ml-auto text-xs text-muted-foreground">First seen {formatDate(req.createdAt)}</span>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Message history</p>
            <div className="space-y-1">
              {req.messages.map((msg) => (
                <div key={msg.id} className="flex gap-2 text-sm">
                  <span className="shrink-0 text-muted-foreground">{formatDate(msg.receivedAt)}</span>
                  <span>{msg.messageBody}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setApproveTarget(req)}>
              Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRejectTarget(req)}>
              Reject
            </Button>
          </div>
        </Card>
      ))}

      {/* Approve dialog */}
      <Dialog open={!!approveTarget} onOpenChange={(open) => !open && setApproveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve phone number</DialogTitle>
            <DialogDescription>
              Approve <span className="font-medium">{approveTarget?.phoneNumber}</span> by entering the person&apos;s name and selecting their child/children.
            </DialogDescription>
          </DialogHeader>
          <ApproveForm
            target={approveTarget}
            students={studentsData?.students ?? []}
            existingGuardians={approvedData?.guardians ?? []}
            isPending={approveMutation.isPending}
            onSubmit={(payload) => {
              if (!approveTarget) return;
              approveMutation.mutate({ id: approveTarget.id, data: payload });
            }}
            onCancel={() => setApproveTarget(null)}
          />
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => !open && setRejectTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject phone number</DialogTitle>
            <DialogDescription>
              Rejecting <span className="font-medium">{rejectTarget?.phoneNumber}</span> removes it from the queue. Its message history will remain in the SMS log for audit. Future texts from this number will stay rejected unless re-approved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={rejectMutation.isPending}
              onClick={() => {
                if (!rejectTarget) return;
                rejectMutation.mutate({ id: rejectTarget.id });
              }}
            >
              {rejectMutation.isPending ? 'Rejecting…' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ApproveForm({
  target,
  students,
  existingGuardians,
  isPending,
  onSubmit,
  onCancel
}: {
  target: PhoneApprovalDetail | null;
  students: { id: string; firstName: string; lastInitial: string; group: string }[];
  existingGuardians: ApprovedNumber[];
  isPending: boolean;
  onSubmit: (payload: { name?: string; guardianId?: string; studentIds: string[] }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = React.useState('');
  const [guardianId, setGuardianId] = React.useState<string | undefined>(undefined);
  const [selectedStudents, setSelectedStudents] = React.useState<Set<string>>(new Set());

  // Reset form when target changes
  React.useEffect(() => {
    setName('');
    setGuardianId(undefined);
    setSelectedStudents(new Set());
  }, [target?.id]);

  const toggleStudent = (id: string) => {
    setSelectedStudents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const canSubmit = (guardianId || name.trim().length > 0) && selectedStudents.size > 0;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="approve-name">Person&apos;s name</Label>
        <Input
          id="approve-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Jane Smith"
          disabled={!!guardianId}
        />
      </div>
      {existingGuardians.length > 0 && (
        <div className="space-y-2">
          <Label>Or link to existing guardian</Label>
          <Select
            value={guardianId ?? '__none__'}
            onValueChange={(val) => setGuardianId(val === '__none__' ? undefined : val)}
          >
            <SelectTrigger>
              <SelectValue placeholder="New guardian" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">New guardian</SelectItem>
              {existingGuardians.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name} ({g.phoneNumber})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-2">
        <Label>Linked children</Label>
        <StudentMultiSelect students={students} selected={selectedStudents} onToggle={toggleStudent} />
        <p className="text-xs text-muted-foreground">
          {selectedStudents.size} child{selectedStudents.size === 1 ? '' : 'ren'} selected
        </p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={!canSubmit || isPending}
          onClick={() => {
            onSubmit({
              name: guardianId ? undefined : name.trim() || undefined,
              guardianId: guardianId || undefined,
              studentIds: [...selectedStudents]
            });
          }}
        >
          {isPending ? 'Approving…' : 'Approve'}
        </Button>
      </DialogFooter>
    </div>
  );
}

function ApprovedTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useListApprovedNumbers();
  const { data: studentsData } = useListStudents();

  const [editTarget, setEditTarget] = React.useState<ApprovedNumber | null>(null);
  const [linkTarget, setLinkTarget] = React.useState<ApprovedNumber | null>(null);
  const [removeTarget, setRemoveTarget] = React.useState<ApprovedNumber | null>(null);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: getListApprovedNumbersQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
  };

  const deleteMutation = useDeleteGuardian({
    mutation: { onSuccess: () => { invalidateAll(); setRemoveTarget(null); } }
  });
  const updateMutation = useUpdateGuardian({
    mutation: { onSuccess: () => { invalidateAll(); setEditTarget(null); } }
  });
  const linkMutation = useLinkGuardianStudent({
    mutation: { onSuccess: () => { invalidateAll(); } }
  });
  const unlinkMutation = useUnlinkGuardianStudent({
    mutation: { onSuccess: () => { invalidateAll(); } }
  });

  const guardians = data?.guardians ?? [];

  return (
    <div className="space-y-4">
      {isLoading && <p className="text-sm text-muted-foreground">Loading approved numbers…</p>}
      {!isLoading && guardians.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-sm text-muted-foreground">No approved numbers yet.</p>
        </Card>
      )}
      {guardians.length > 0 && (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Linked children</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {guardians.map((g) => (
                <TableRow key={g.id}>
                  <TableCell className="font-medium">{g.name}</TableCell>
                  <TableCell className="font-mono text-sm">{g.phoneNumber}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {g.students.length === 0 && (
                        <span className="text-xs text-muted-foreground">None</span>
                      )}
                      {g.students.map((s) => (
                        <Badge key={s.id} variant="secondary" className="gap-1">
                          {s.firstName} {s.lastInitial}.
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => unlinkMutation.mutate({ id: g.id, studentId: s.id })}
                          >
                            <XIcon className="size-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditTarget(g)}>
                        <PencilIcon className="size-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setLinkTarget(g)}>
                        <UserPlusIcon className="size-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRemoveTarget(g)}>
                        <TrashIcon className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit guardian</DialogTitle>
            <DialogDescription>Update the name or phone number for this approved contact.</DialogDescription>
          </DialogHeader>
          <EditGuardianForm
            target={editTarget}
            isPending={updateMutation.isPending}
            onSubmit={(payload) => {
              if (!editTarget) return;
              updateMutation.mutate({ id: editTarget.id, data: payload });
            }}
            onCancel={() => setEditTarget(null)}
          />
        </DialogContent>
      </Dialog>

      {/* Link children dialog */}
      <Dialog open={!!linkTarget} onOpenChange={(open) => !open && setLinkTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link children to {linkTarget?.name}</DialogTitle>
            <DialogDescription>Select children to link to this guardian.</DialogDescription>
          </DialogHeader>
          <LinkStudentsForm
            target={linkTarget}
            allStudents={studentsData?.students ?? []}
            isPending={linkMutation.isPending}
            onSubmit={(studentIds) => {
              if (!linkTarget) return;
              for (const sid of studentIds) {
                linkMutation.mutate({ id: linkTarget.id, studentId: sid });
              }
              setLinkTarget(null);
            }}
            onCancel={() => setLinkTarget(null)}
          />
        </DialogContent>
      </Dialog>

      {/* Remove dialog */}
      <Dialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove approved number</DialogTitle>
            <DialogDescription>
              Removing <span className="font-medium">{removeTarget?.name}</span> ({removeTarget?.phoneNumber}) deletes the guardian record and unlinks all children. Future texts from this number will be treated as unknown and require re-approval. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (!removeTarget) return;
                deleteMutation.mutate({ id: removeTarget.id });
              }}
            >
              {deleteMutation.isPending ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditGuardianForm({
  target,
  isPending,
  onSubmit,
  onCancel
}: {
  target: ApprovedNumber | null;
  isPending: boolean;
  onSubmit: (payload: { name?: string; phoneNumber?: string }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');

  React.useEffect(() => {
    setName(target?.name ?? '');
    setPhone(target?.phoneNumber ?? '');
  }, [target?.id]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="edit-name">Name</Label>
        <Input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="edit-phone">Phone number</Label>
        <Input id="edit-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+61412345678" />
        <p className="text-xs text-muted-foreground">E.164 format (e.g. +61412345678)</p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={isPending}
          onClick={() => onSubmit({ name: name.trim() || undefined, phoneNumber: phone.trim() || undefined })}
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </div>
  );
}

function LinkStudentsForm({
  target,
  allStudents,
  isPending,
  onSubmit,
  onCancel
}: {
  target: ApprovedNumber | null;
  allStudents: { id: string; firstName: string; lastInitial: string; group: string }[];
  isPending: boolean;
  onSubmit: (studentIds: string[]) => void;
  onCancel: () => void;
}) {
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    setSelected(new Set());
  }, [target?.id]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Filter out students already linked to this guardian
  const linkedIds = new Set(target?.students.map((s) => s.id) ?? []);
  const available = allStudents.filter((s) => !linkedIds.has(s.id));

  return (
    <div className="space-y-4">
      <StudentMultiSelect students={available} selected={selected} onToggle={toggle} />
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={selected.size === 0 || isPending}
          onClick={() => onSubmit([...selected])}
        >
          <PlusIcon className="size-4" /> Link {selected.size > 0 ? `(${selected.size})` : ''}
        </Button>
      </DialogFooter>
    </div>
  );
}

export function ApprovedNumbersPage() {
  const [activeTab, setActiveTab] = React.useState('pending');
  const { data: pendingData } = useListPhoneApprovals({ status: 'pending' });
  const pendingCount = pendingData?.total ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Approved Numbers"
        description="Manage the approved SMS sender list and approve unknown numbers"
        icon={PhoneIcon}
      />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">
            Pending{pendingCount > 0 && ` (${pendingCount})`}
          </TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
        </TabsList>
        <TabsContent value="pending">
          <PendingTab onSwitchToApproved={() => setActiveTab('approved')} />
        </TabsContent>
        <TabsContent value="approved">
          <ApprovedTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
